import { Loader2, XIcon } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { useAppWindowInsetsStore } from 'src/app/router/layouts/navigation/app-window-frame'
import { paths } from 'src/config/paths'
import { useCreateBusiness } from 'src/features/business/create/api/use-create-business'
import { BusinessCreateContext } from 'src/features/business/create/context/business-create-context'
import { useBusinessCreateStore } from 'src/features/business/create/store/business-create-store'
import { ChevronDownIcon } from 'src/shared/components/icons/chevron-down-icon'
import { Button } from 'src/shared/components/ui/buttons/button'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from 'src/shared/utils/cn'

function Header({ showBackButton = true }: { showBackButton?: boolean }) {
  const navigate = useNavigate()
  const { insets } = useAppWindowInsetsStore()
  const { handleBack } = useContext(BusinessCreateContext)

  const handleCloseClick = () => {
    navigate(paths.business.path)
  }

  return (
    <div
      className="fixed flex flex-row justify-between p-[38px]"
      style={{
        top: insets.top,
        left: insets.left,
        right: insets.right,
      }}
    >
      {showBackButton ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          className="size-[60px] bg-[#F8F7F5] hover:bg-[#F8F7F5]"
        >
          <ChevronDownIcon className="size-6 rotate-90" />
        </Button>
      ) : (
        <div> {/* empty div, dont remove, used for spacing */} </div>
      )}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleCloseClick}
        className="size-[60px] bg-[#F8F7F5] hover:bg-[#F8F7F5]"
      >
        <XIcon className="size-8" />
      </Button>
    </div>
  )
}

function Footer({
  buttonText = 'Next',
  isLoading = false,
  percentage,
}: {
  buttonText?: string
  isLoading?: boolean
  percentage: number
}) {
  const { insets } = useAppWindowInsetsStore()

  return (
    <div
      className="fixed flex h-[100px] flex-col items-end justify-end rounded-b-[20px] border-t border-[#D8D8D6] bg-white"
      style={{
        bottom: insets.bottom,
        left: insets.left,
        right: insets.right,
      }}
    >
      <ProgressBar percentage={percentage} />
      <div className="flex flex-col p-[22px]">
        <Button
          variant="default"
          size="xl"
          className="w-[250px] rounded-full bg-primary text-base font-semibold text-white"
          form="business-create-form"
          disabled={isLoading}
        >
          {isLoading ? <Loader2 className="size-4 animate-spin" /> : buttonText}
        </Button>
      </div>
    </div>
  )
}

function ProgressBar({ percentage }: { percentage: number }) {
  return (
    <div className="h-[4px] w-full shrink-0 bg-[#D8D8D6]">
      <div
        className="h-full rounded-r-full bg-primary transition-all duration-300"
        style={{ width: `${percentage}%` }}
      ></div>
    </div>
  )
}

const DISSOLVE_DURATION = 150

const DISSOLVE_STYLE = {
  transitionDuration: `${DISSOLVE_DURATION}ms`,
  animationDuration: `${DISSOLVE_DURATION}ms`,
}

export default function BusinessCreateLayout() {
  const { isMobile } = useBreakpoint()
  const { step } = useParams()
  const { reset, totalSteps, getFormData } = useBusinessCreateStore()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { mutate: createBusiness } = useCreateBusiness()
  // Used to dissolve the current page when the user navigates to the next page.
  const [dissolve, setDissolve] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    return () => {
      reset()
    }
  }, [reset])

  const handleBack = () => {
    setDissolve(true)
    const currentStep = Number(step)
    if (currentStep === 1) {
      navigate(paths.business.path)
      return
    }
    setTimeout(() => {
      navigate(paths.business.create.getHref(String(Number(step) - 1)))
      setDissolve(false)
    }, DISSOLVE_DURATION)
  }

  const handleNext = (skip = 0) => {
    const formData = getFormData()
    setDissolve(true)

    const currentStep = Number(step) + skip

    if (currentStep >= totalSteps) {
      setIsLoading(true)

      // Get the coordinates from the first provided location.
      let coordinates: { lat: number; lng: number } | undefined
      const exactLocations = formData?.exactLocations
      const desiredLocations = formData?.desiredLocations
      if (exactLocations && exactLocations.length > 0) {
        coordinates = {
          lat: exactLocations[0].latitude,
          lng: exactLocations[0].longitude,
        }
      } else if (desiredLocations && desiredLocations.length > 0) {
        coordinates = {
          lat: desiredLocations[0].latitude,
          lng: desiredLocations[0].longitude,
        }
      }

      createBusiness(formData || {}, {
        onSuccess: (response) => {
          navigate(paths.business.path, {
            state: { type: 'createSuccess', business: response, coordinates },
          })
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Failed to create business',
          })
        },
        onSettled: () => {
          setIsLoading(false)
        },
      })
      return
    }

    setTimeout(() => {
      navigate(paths.business.create.getHref(String(Number(step) + 1 + skip)))
      setDissolve(false)
    }, DISSOLVE_DURATION)
  }

  if (isMobile) {
    return (
      <div className={cn('size-full transition-opacity', dissolve && 'opacity-0')} style={DISSOLVE_STYLE}>
        <Outlet />
      </div>
    )
  }

  const percentage = Math.round((Number(step) / totalSteps) * 100)

  return (
    <BusinessCreateContext.Provider value={{ handleNext, handleBack }}>
      <div className="size-full">
        <Header />
        <div className={cn('size-full pb-[100px] transition-opacity', dissolve && 'opacity-0')} style={DISSOLVE_STYLE}>
          <Outlet />
        </div>
        <Footer isLoading={isLoading} percentage={percentage} />
      </div>
    </BusinessCreateContext.Provider>
  )
}
